/**
 * SliceType - Enumeration of slice types
 *
 * Defines the different types of slices that can be created in the
 * waffle construction system.
 *
 * Based on cc.sketchchair.core.CrossSliceSelection.java
 */

/**
 * Slice type enumeration
 * @readonly
 * @enum {number}
 */
export const SliceType = Object.freeze({
    /**
     * Multiple parallel slices between two points
     * Use case: Primary chair structure (e.g., 15 slices from front to back)
     * Input: Start position, end position, number of slices OR spacing distance
     */
    SLICES: 0,

    /**
     * Leg reinforcement slices
     * Special behavior: Slots tied to legs have priority in collision detection
     * Use case: Extra support at chair legs
     */
    LEG: 1,

    /**
     * Plane positioned along the edge of a shape
     * Special behavior: Different slot handling than standard planes
     * Use case: Edge reinforcements
     */
    PLANE_ON_EDGE: 2,

    /**
     * Single cross-section at one position
     * Use case: Adding a single reinforcement layer
     * Input: Click position on sketch (0.0-1.0 normalized)
     */
    SINGLE_SLICE: 3,

    /**
     * Horizontal slats with finger joints
     * Use case: Connecting vertical slices with horizontal supports
     * Input: Guide path, start/end positions, number of slats OR spacing, teeth count
     * Output: Slat SlicePlanes + finger joint slots on both slats and vertical slices
     */
    SLATSLICES: 4,

    /**
     * General purpose single plane
     * Use case: Custom positioning, special features
     */
    PLANE: 5
});

/**
 * Get human-readable name for slice type
 * @param {number} type - Slice type value
 * @returns {string} Type name
 */
export function getSliceTypeName(type) {
    const names = {
        [SliceType.SLICES]: 'Slice Group',
        [SliceType.LEG]: 'Leg Support',
        [SliceType.PLANE_ON_EDGE]: 'Edge Plane',
        [SliceType.SINGLE_SLICE]: 'Single Slice',
        [SliceType.SLATSLICES]: 'Slat Group',
        [SliceType.PLANE]: 'Plane'
    };
    return names[type] || 'Unknown';
}

/**
 * Check if slice type creates multiple planes
 * @param {number} type - Slice type value
 * @returns {boolean} True if creates multiple planes
 */
export function isMultiPlaneType(type) {
    return type === SliceType.SLICES || type === SliceType.SLATSLICES;
}

/**
 * Check if slice type requires guide path
 * @param {number} type - Slice type value
 * @returns {boolean} True if requires path
 */
export function requiresGuidePath(type) {
    return type !== SliceType.PLANE;
}
